import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  CoachAthleteCard,
  CoachDashboardData,
  CoachUnavailableAthlete,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Avatar } from "../primitives/Avatar";
import { Card } from "../primitives/Card";
import { EmptyState } from "../primitives/EmptyState";
import { StatPair } from "../primitives/StatPair";
import { AthleteCardRow } from "./AthleteCardRow";

// <CoachDashboard> (CO-8) — the coach console team overview: a KPI strip over a
// priority-sorted athlete list. Presentational + shared-first (thin web-rnw /
// future mobile mounts); the data is assembled by buildCoachDashboard and the
// numbers reuse the athlete's own weekly-target math, so nothing here can drift
// from the athlete's Home meter. Every panel EXPOSES a fact — no coaching
// judgement is made here (that's the coach's read). One list sorted
// attention-first; the per-athlete detail (CO-9) opens via onSelectAthlete.
// The row itself lives in AthleteCardRow (shared with the CO-9 detail header).

// The loading/error gate lives HERE, not in each host (CN-coach-dashboard-gate):
// `data` is null until the fan-out lands, so every mount was hand-rolling the
// same placeholder + the same cards.find() to turn a sub back into a card. Two
// copies of a gate is how a fix lands on one platform only. Hosts now render
// this unconditionally and get the card, not the sub — which is what both of
// them actually wanted (web renders the detail as a child, mobile pushes it).
export type CoachDashboardProps = {
  theme: UiTheme;
  data: CoachDashboardData | null;
  loading?: boolean;
  error?: boolean;
  onSelectAthlete?: (card: CoachAthleteCard) => void;
};

export function CoachDashboard({
  theme,
  data,
  loading,
  error,
  onSelectAthlete,
}: CoachDashboardProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  // Gate on having nothing to show, not on `loading`: once a roster is on
  // screen, a re-fetch keeps it there and the host's own refresh affordance
  // carries the "working" cue. Replacing a good list with a spinner every time
  // is what makes a refresh feel like a reset.
  if (!data) {
    return (
      <View style={styles.center}>
        <Text style={styles.status}>
          {t(
            error && !loading
              ? "coach.dashboard.loadFailed"
              : "coach.dashboard.loading",
          )}
        </Text>
      </View>
    );
  }

  return (
    <CoachDashboardBody
      theme={theme}
      data={data}
      refreshFailed={!!error && !loading}
      onSelectAthlete={onSelectAthlete}
    />
  );
}

function CoachDashboardBody({
  theme,
  data,
  refreshFailed,
  onSelectAthlete,
}: {
  theme: UiTheme;
  data: CoachDashboardData;
  // The data on screen is the last good read: the newest one failed.
  refreshFailed: boolean;
  onSelectAthlete?: (card: CoachAthleteCard) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { kpis, cards, unavailable } = data;

  const kpiTiles = [
    { label: t("coach.dashboard.kpiAssigned"), value: String(kpis.assigned) },
    {
      label: t("coach.dashboard.kpiActive"),
      // Out of the athletes that loaded: the others are in no count but this.
      value: `${kpis.activeLast7d}/${cards.length}`,
    },
    {
      label: t("coach.dashboard.kpiAttention"),
      value: String(kpis.attention),
      alert: kpis.attention > 0,
    },
    {
      label: t("coach.dashboard.kpiAdherence"),
      value:
        kpis.avgAdherencePct != null ? `${kpis.avgAdherencePct}%` : "—",
    },
    {
      label: t("coach.dashboard.kpiSessions"),
      value: String(kpis.sessionsLast7d),
    },
  ];

  return (
    <View style={styles.wrap}>
      <Text style={styles.h1}>{t("coach.dashboard.title")}</Text>
      {refreshFailed ? (
        <Text style={styles.note}>{t("coach.loadFailed")}</Text>
      ) : null}

      <View style={styles.strip}>
        {kpiTiles.map((tile) => (
          <View key={tile.label} style={styles.kpiCell}>
            <Card theme={theme}>
              <StatPair
                theme={theme}
                value={tile.value}
                label={tile.label}
                valueColor={tile.alert ? theme.palette.danger : undefined}
                labelLines={2}
              />
            </Card>
          </View>
        ))}
      </View>
      {unavailable.length > 0 ? (
        <Text style={styles.note}>
          {t("coach.dashboard.kpiNotLoaded", { count: unavailable.length })}
        </Text>
      ) : null}

      {kpis.assigned === 0 ? (
        <Card theme={theme}>
          <EmptyState
            theme={theme}
            title={t("coach.dashboard.empty")}
            sub={t("coach.dashboard.emptySub")}
          />
        </Card>
      ) : (
        <View style={styles.list}>
          {cards.map((card) => (
            <AthleteCardRow
              key={card.sub}
              theme={theme}
              card={card}
              // The row hands back a sub; we're already holding the card it came
              // from, so hosts never have to look it up again.
              onPress={onSelectAthlete ? () => onSelectAthlete(card) : undefined}
            />
          ))}
          {unavailable.map((athlete) => (
            <UnavailableRow key={athlete.sub} theme={theme} athlete={athlete} />
          ))}
        </View>
      )}
    </View>
  );
}

// An athlete whose sessions could not be read: the name and the retry copy, no
// numbers. Not pressable: the detail would open on the same missing read.
function UnavailableRow({
  theme,
  athlete,
}: {
  theme: UiTheme;
  athlete: CoachUnavailableAthlete;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const message = t("coach.loadFailed");
  return (
    <View accessible accessibilityLabel={`${athlete.name}, ${message}`}>
      <Card theme={theme}>
        <View style={styles.unavailable}>
          <Avatar theme={theme} name={athlete.name} tone="neutral" />
          <View style={styles.unavailableText}>
            <Text style={styles.unavailableName} numberOfLines={1}>
              {athlete.name}
            </Text>
            <Text style={styles.note}>{message}</Text>
          </View>
        </View>
      </Card>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s4, padding: s.s4 },
    h1: {
      color: p.ink,
      fontSize: ty.sizes.lg,
      fontWeight: "700",
    },
    // flexWrap grid: tiles keep a min width and grow to fill, so a wide viewport
    // fits all five on one row and a phone reflows to 2–3 — no Platform branch.
    strip: { flexDirection: "row", flexWrap: "wrap", gap: s.s3 },
    kpiCell: { flexGrow: 1, flexBasis: 130, minWidth: 130 },
    list: { gap: s.s3 },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s6,
    },
    // base, like body text everywhere else. It was briefly bumped on the theory
    // that a phone has no zoom escape hatch — it does: RN honours the system
    // font scale, which is exactly how this user reads. Oversizing here would
    // only make the coach's status line louder than the app's own body copy.
    status: { color: p.ink3, fontSize: ty.sizes.base, textAlign: "center" },
    note: { color: p.ink3, fontSize: ty.sizes.sm },
    unavailable: { flexDirection: "row", alignItems: "center", gap: s.s4 },
    unavailableText: { flex: 1, minWidth: 0, gap: s.s1 },
    unavailableName: { color: p.ink, fontSize: ty.sizes.md, fontWeight: "700" },
  });
}
